import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";
import { Text } from "../Text/Text";

type AvatarColor = "a1" | "a2" | "a3" | "a4" | "a5";
type AvatarAppearance = "default" | "inverse";

const inverseColorClasses: Record<AvatarColor, string> = {
  a1: "bg-avatar-fill-a1-on-bg text-avatar-fill-a1-bg",
  a2: "bg-avatar-fill-a2-on-bg text-avatar-fill-a2-bg",
  a3: "bg-avatar-fill-a3-on-bg text-avatar-fill-a3-bg",
  a4: "bg-avatar-fill-a4-on-bg text-avatar-fill-a4-bg",
  a5: "bg-avatar-fill-a5-on-bg text-avatar-fill-a5-bg",
};

const avatarVariants = cva(
  "inline-flex items-center justify-center font-medium text-center select-none",
  {
    variants: {
      color: {
        a1: "bg-avatar-fill-a1-bg text-avatar-fill-a1-on-bg",
        a2: "bg-avatar-fill-a2-bg text-avatar-fill-a2-on-bg",
        a3: "bg-avatar-fill-a3-bg text-avatar-fill-a3-on-bg",
        a4: "bg-avatar-fill-a4-bg text-avatar-fill-a4-on-bg",
        a5: "bg-avatar-fill-a5-bg text-avatar-fill-a5-on-bg",
      },
      size: {
        small: "h-[24px] w-[24px] text-body-medium-regular rounded-large",
        medium: "h-[32px] w-[32px] text-body-medium-regular rounded-xlarge",
        xlarge: "h-[36px] w-[36px] text-body-medium-regular rounded-xlarge",
      },
    },
    defaultVariants: {
      color: "a1",
      size: "medium",
    },
  }
);

const statusVariants = cva(
  "absolute flex items-center justify-center rounded-full border-2 border-surface-fill-neutral-intense",
  {
    variants: {
      size: {
        small: "h-5 w-5 -bottom-0.5 -right-0.5",
        medium: "h-6 w-6 -bottom-1 -right-1",
        xlarge: "h-6 w-6 -bottom-1 -right-1",
      },
      statusColor: {
        positive: "bg-action-fill-positive-default",
        negative: "bg-action-fill-negative-default",
        notice: "bg-action-fill-notice-default",
        info: "bg-action-fill-info-default",
        neutral: "bg-action-fill-neutral-default",
      },
    },
    defaultVariants: {
      size: "medium",
      statusColor: "notice",
    },
  }
);

export interface AvatarProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {
  /**
   * The color variant of the avatar
   */
  color?: AvatarColor;
  /**
   * Whether to invert the background/text colors
   */
  appearance?: AvatarAppearance;
  /**
   * The size of the avatar
   */
  size?: "small" | "medium" | "xlarge";
  /**
   * The initials or text to display in the avatar
   */
  children?: React.ReactNode;
  /**
   * Image source for the avatar (takes precedence over children)
   */
  src?: string;
  /**
   * Alt text for the avatar image
   */
  alt?: string;
  /**
   * Whether to show a status indicator
   */
  showStatus?: boolean;
  /**
   * The color of the status indicator
   */
  statusColor?: "positive" | "negative" | "notice" | "info" | "neutral";
  /**
   * Custom icon or content for the status indicator
   */
  statusIcon?: React.ReactNode;
  /**
   * Optional text label to display next to the avatar
   */
  label?: string;
  /**
   * Optional trailing component (icon or other element) to display after the label
   */
  trailingComponent?: React.ReactNode;
  /**
   * Custom class name for the avatar
   */
  className?: string;
  /**
   * Custom class name for the container (when label or trailingComponent is provided)
   */
  containerClassName?: string;
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      className,
      appearance = "default",
      color,
      size,
      children,
      src,
      alt,
      showStatus = false,
      statusColor = "notice",
      statusIcon,
      label,
      trailingComponent,
      containerClassName,
      ...props
    },
    ref
  ) => {
    const [imageError, setImageError] = React.useState(false);

    const handleImageError = () => {
      setImageError(true);
    };

    const getStatusIconSize = () => {
      switch (size) {
        case "small":
          return "h-3.5 w-3.5";
        case "medium":
          return "h-4 w-4";
        default:
          return "h-4 w-4";
      }
    };

    const getTextSize = () => {
      switch (size) {
        case "small":
          return "small";
        case "medium":
          return "medium";
        default:
          return "medium";
      }
    };

    const resolvedColor: AvatarColor = color ?? "a1";
    const avatarElement = (
      <div className="relative inline-block">
        <div
          className={cn(
            avatarVariants({ color: resolvedColor, size }),
            appearance === "inverse"
              ? inverseColorClasses[resolvedColor]
              : undefined,
            className
          )}
          {...props}
        >
          {src && !imageError ? (
            <img
              src={src}
              alt={alt || "Avatar"}
              className={cn(
                "h-full w-full object-cover",
                size === "small" ? "rounded-large" : "rounded-xlarge"
              )}
              onError={handleImageError}
            />
          ) : (
            children
          )}
        </div>
        {showStatus && (
          <div className={cn(statusVariants({ size, statusColor }))}>
            {statusIcon && (
              <span
                className={cn(
                  "text-action-ink-on-primary-normal",
                  getStatusIconSize()
                )}
              >
                {statusIcon}
              </span>
            )}
          </div>
        )}
      </div>
    );

    // If no label or trailing component, return just the avatar
    if (!label && !trailingComponent) {
      return <div ref={ref}>{avatarElement}</div>;
    }

    // Otherwise, return avatar with label and/or trailing component
    return (
      <div
        ref={ref}
        className={cn("inline-flex items-center gap-3", containerClassName)}
      >
        {avatarElement}
        {label && (
          <Text
            variant="body"
            size={getTextSize()}
            weight="medium"
            color="default"
            as="span"
          >
            {label}
          </Text>
        )}
        {trailingComponent && (
          <span className="ml-auto">{trailingComponent}</span>
        )}
      </div>
    );
  }
);

Avatar.displayName = "Avatar";

export { Avatar, avatarVariants };
